import React from 'react';
import { Box, Flex, HStack, Text, Icon, Image } from '@chakra-ui/react';
import { FiCamera, FiMic, FiVideo, FiFileText, FiSmile } from 'react-icons/fi';
import { useMediaRegistry } from './mediaRegistry';

/**
 * The quoted block WhatsApp draws above a reply: a coloured bar, who said it,
 * one line of what, and a thumbnail if it was a photo.
 *
 * Used in two places with the same look — inside a bubble (`context.wamid`)
 * and in the composer's reply strip — so a reply looks the same before and
 * after it is sent.
 *
 * The quoted message is resolved from the messages already on screen. One
 * that lives further up than has been loaded cannot be shown, and the block
 * says so rather than pretending.
 */
const MEDIA_LABEL = {
	image: { icon: FiCamera, label: 'Photo' },
	sticker: { icon: FiSmile, label: 'Sticker' },
	audio: { icon: FiMic, label: 'Voice message' },
	video: { icon: FiVideo, label: 'Video' },
	document: { icon: FiFileText, label: 'Document' },
};

export function quotePreview(message) {
	if (!message) return { icon: null, text: '' };
	const media = MEDIA_LABEL[message.type];
	if (media) {
		return { icon: media.icon, text: message.media?.caption || message.media?.filename || media.label };
	}
	if (message.type === 'template') return { icon: null, text: message.content || 'Template message' };
	return { icon: null, text: message.content || `[${message.type}]` };
}

export default function QuotedMessage({ message, wamid, senderLabel, onClick, onClose, compact }) {
	const getMedia = useMediaRegistry();
	const { icon, text } = quotePreview(message);
	const thumb =
		message && (message.type === 'image' || message.type === 'sticker')
			? getMedia(message.wamid)?.url
			: null;

	return (
		<Flex
			role={onClick ? 'button' : undefined}
			tabIndex={onClick ? 0 : undefined}
			onClick={onClick}
			onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
			cursor={onClick ? 'pointer' : 'default'}
			bg="wa.reply"
			borderLeftWidth="4px"
			borderLeftColor="wa.replyBorder"
			borderRadius="6px"
			overflow="hidden"
			mb={compact ? 0 : '1.5'}
			minH="42px"
			maxW="100%"
			_hover={onClick ? { filter: 'brightness(0.97)' } : undefined}
			transition="filter 120ms ease"
		>
			<Box flex="1" minW="0" px="2.5" py="1.5">
				<Text fontSize="xs" fontWeight="semibold" color="wa.replyBorder" noOfLines={1}>
					{senderLabel}
				</Text>
				{message ? (
					<HStack spacing="1" minW="0">
						{icon && <Icon as={icon} boxSize="3" color="wa.inkMuted" flexShrink={0} />}
						<Text fontSize="xs" color="wa.inkMuted" noOfLines={compact ? 1 : 2} wordBreak="break-word">
							{text}
						</Text>
					</HStack>
				) : (
					<Text fontSize="xs" color="wa.inkMuted" fontStyle="italic">
						{wamid ? 'Message not loaded — scroll up to load it' : 'Original message unavailable'}
					</Text>
				)}
			</Box>
			{thumb && <Image src={thumb} alt="" w="50px" h="50px" objectFit="cover" flexShrink={0} />}
			{onClose && (
				<Box
					as="button"
					type="button"
					aria-label="Cancel reply"
					px="2"
					color="wa.inkMuted"
					_hover={{ color: 'wa.ink' }}
					onClick={(e) => {
						e.stopPropagation();
						onClose();
					}}
				>
					×
				</Box>
			)}
		</Flex>
	);
}
